import { createContext } from 'react';
import { ActorRefFrom, assign } from 'xstate';
import { useActorRef } from '@xstate/react';
import { FCWithChildren } from '../../lib/types';
import { useFetch } from '../../lib/hooks/apiFetch';
import { useNamespace } from '../../lib/hooks/useNamespace';
import { useHasReviewedApplication } from '../../lib/hooks/useHasReviewedApplication';
import { useAppConfig } from '../AppConfigContext';
import { useAppData } from '../AppDataContext';
import { useBuyerMachine } from '../BuyerMachineContext';
import { useFeatureFlags } from '../FeatureFlagsContext';
import { useLocale } from '../IntlContext';
import {
  useOrder,
  useLocationID,
  useMerchantID,
  useCartID,
  useMerchantOrigin,
  useProgramValues,
  useMerchantPaymentProducts,
  useMerchantDetails,
  useSDKCallbacks,
  useVirtualCard,
  useExchangedOptions,
} from '../XPropsContext';
import { useAuthentication } from '../AuthenticationMachineContext';
import { ApplicationContext, applicationMachine } from './applicationMachine';
import {
  assignApplication,
  assignApplicationWithAppendedStatusCodes,
  assignChildMachineReady,
  assignGeneratedDisclosures,
  assignSelectedOffer,
  createAssignStateMachineActorRefs,
} from './assigns';
import { useFraudAlloyMachineLogic } from './machines/fraudAlloy/useFraudAlloyMachineLogic';
import {
  createApplicationCheckout,
  createApplicationPrepareCheckout,
  createCreateApplication,
  createFetchApplication,
  createFetchLatestApplication,
  createLocateExchangedOfferAndGenerateDisclosures,
  prepareInStoreApplication,
  createPrepareVirtualCardIssuance,
  createReCreateApplication,
  createReCreateInStoreContingentApplication,
  getErrorStatusCodeFromApplication,
} from './actors';
import {
  createEmitCreatedApplicationEstimatedSpend,
  createHandleOnApproved,
  createHandleOnCheckout,
  createHasReviewedApplication,
  createLogError,
  emitFetchedApplicationEstimatedSpend,
  handleApplicationAnalytics,
  handleApplicationCheckoutCompleteAnalytics,
  handleApplicationOrderSubmittedAnalytics,
  handleApplicationDone,
  handleApplicationServiceError,
  handleAutoPayEnrollmentSuccess,
  handleSelectedOfferAnalytics,
  processDeferredInitializationEvents,
  stopStateMachineActorRefs,
} from './actions';
import {
  createHasExchangedPaymentAgreementId,
  createIsApprovedContingentInStore,
  createIsBuyerReadyAndComplete,
  createIsCompleteMismatchedBuyer,
  createIsInvalidApplication,
  createIsUnexpiredInStoreApplication,
  createIsValidApplicationAndMatchesPreviouslyApprovedOrder,
  createIsVirtualCardApplication,
  createNeedsIDVerificationAlloy,
  guardIsApproved,
  guardNeedsFullIIN,
  isAgeAlabamaMilitary,
  isAgeIneligible,
  isAlloySystemOutage,
  isBuyerHashFailed,
  isBuyerSkippedInstallments,
  isBuyerStatusIneligible,
  isCapacityRecheck,
  isCheckoutCompleted,
  isCheckoutPrepared,
  isCreditDenial,
  isCreditFreeze,
  isDownPaymentAuthDeclinedError,
  isErrorResult,
  isFraudAlertDenial,
  isFraudDenial,
  isHybridLineOfCreditOfferSelected,
  isIneligibleAddress,
  isInstallmentOfferSelected,
  isInvalidAddressNoMatchIneligible,
  isKycDenial,
  isLocationIneligible,
  isMaxCardIneligible,
  isNeedsActionWarning,
  isNeedsAdditionalInformationWarning,
  isOutstandingLoansIneligible,
  isPoBoxAddressIneligible,
  isPreviousDenialIneligible,
  isSanctionsDenial,
  isSanctionsOFACDenial,
  isSplitPayOfferSelected,
  requiredChildMachinesReady,
} from './guards';
import { useOrderUpdateEventEmitter } from './hooks/useOrderUpdateEventEmitter';
import { useBuyerMachineEventEmitter } from './hooks/useBuyerMachineEventEmitter';
import { usePaymentsMachineLogic } from './machines/payments/usePaymentsMachineLogic';

export type ApplicationMachineActorRef = ActorRefFrom<
  typeof applicationMachine
>;

export const ApplicationMachineContext =
  createContext<ApplicationMachineActorRef | null>(null);

export type ApplicationProviderProps = {
  context?: Partial<ApplicationContext>;
};

export const ApplicationMachineProvider: FCWithChildren<ApplicationProviderProps> =
  ({ children, context = {} }) => {
    const { state: authState } = useAuthentication();
    const {
      state: buyerState,
      buyer: contextBuyer,
      buyerID: contextBuyerID,
    } = useBuyerMachine();
    const { setVirtualCardIssuance } = useAppData();
    const { order } = useOrder();
    const { enableIDVerification } = useAppConfig();
    const apiFetch = useFetch();
    const locationID = useLocationID();
    const { merchantID } = useMerchantID();
    const cartID = useCartID();
    const { merchantOrigin } = useMerchantOrigin();
    const program = useProgramValues();
    const { locale } = useLocale();
    const { merchantPaymentProducts } = useMerchantPaymentProducts();
    const { canViewCapacity } = useMerchantDetails();
    const { onApproved, onCheckout } = useSDKCallbacks();
    const { isVirtualCard } = useVirtualCard();
    const namespace = useNamespace();
    const { setHasReviewedApplication } = useHasReviewedApplication();
    const { isExchangedStreamlined, onCheckoutDelayMs } = useFeatureFlags();
    const fraudAlloyMachineLogic = useFraudAlloyMachineLogic();
    const paymentsMachineLogic = usePaymentsMachineLogic();
    const { paymentAgreementID } = useExchangedOptions();

    const initialContext: ApplicationContext = {
      application: context.application || null,
      selectedOffer: context.selectedOffer || null,
      generatedDisclosures: context.generatedDisclosures || null,
      stateMachineActorRefs: {
        estimatedSpendMachineActorRef: null,
        fraudAlloyMachineActorRef: null,
        paymentsMachineActorRef: null,
      },
      childMachinesReady: new Set(),
      childMachinesRequired: ['fraudAlloyMachine'],
      deferredInitializationEvents: [],
    };

    const applicationService = useActorRef(
      applicationMachine.provide({
        guards: {
          isInstallmentOfferSelected,
          isSplitPayOfferSelected,
          isHybridLineOfCreditOfferSelected,
          isIneligibleAddress,
          needsFullIIN: guardNeedsFullIIN,
          isApproved: guardIsApproved,
          isCheckoutCompleted,
          isDownPaymentAuthDeclinedError,
          isCheckoutPrepared,
          isErrorResult,
          isPoBoxAddressIneligible,
          isInvalidAddressNoMatchIneligible,
          isBuyerHashFailed,
          isCreditFreeze,
          isKycDenial,
          isPreviousDenialIneligible,
          isMaxCardIneligible,
          isOutstandingLoansIneligible,
          isAgeIneligible,
          isAgeAlabamaMilitary,
          isBuyerStatusIneligible,
          isBuyerSkippedInstallments,
          isLocationIneligible,
          isNeedsActionWarning,
          isNeedsAdditionalInformationWarning,
          isFraudDenial,
          isCreditDenial,
          isSanctionsOFACDenial,
          isSanctionsDenial,
          isFraudAlertDenial,
          isCapacityRecheck,
          isAlloySystemOutage,
          requiredChildMachinesReady,
          isUnexpiredInStoreApplication: createIsUnexpiredInStoreApplication({
            namespace,
            isVirtualCard,
          }),
          isInvalidApplication: createIsInvalidApplication({
            namespace,
            contextBuyerID,
            merchantPaymentProducts,
            isVirtualCard,
          }),
          isCompleteMismatchedBuyer: createIsCompleteMismatchedBuyer({
            authState,
            buyerState,
          }),
          isValidApplicationAndMatchesPreviouslyApprovedOrder:
            createIsValidApplicationAndMatchesPreviouslyApprovedOrder({
              namespace,
              contextBuyerID,
              merchantPaymentProducts,
              isVirtualCard,
              order,
              locale,
            }),
          hasExchangedPaymentAgreementId: createHasExchangedPaymentAgreementId({
            paymentAgreementID,
            contextBuyerID,
          }),
          isBuyerReadyAndComplete: createIsBuyerReadyAndComplete({
            buyerState,
          }),
          isApprovedContingentInStore: createIsApprovedContingentInStore({
            namespace,
          }),
          needsIDVerificationAlloy: createNeedsIDVerificationAlloy({
            enableIDVerification,
          }),
          isVirtualCardApplication: createIsVirtualCardApplication({
            isVirtualCard,
          }),
        },
        actions: {
          assignApplication,
          assignApplicationWithAppendedStatusCodes,
          assignGeneratedDisclosures,
          assignSelectedOffer,
          assignChildMachineReady,
          assignStateMachineActorRefs: createAssignStateMachineActorRefs({
            fraudAlloyMachineLogic,
            paymentsMachineLogic,
          }),
          resetContext: assign({
            ...initialContext,
          }),
          stopStateMachineActorRefs,
          handleApplicationDone,
          handleApplicationServiceError,
          handleApplicationAnalytics,
          handleApplicationCheckoutCompleteAnalytics,
          handleApplicationOrderSubmittedAnalytics,
          handleAutoPayEnrollmentSuccess,
          handleSelectedOfferAnalytics,
          emitCreatedApplicationEstimatedSpend:
            createEmitCreatedApplicationEstimatedSpend({
              merchantPaymentProducts,
            }),
          emitFetchedApplicationEstimatedSpend,
          handleOnApproved: createHandleOnApproved({
            onApproved,
            canViewCapacity,
          }),
          handleOnCheckout: createHandleOnCheckout({
            onCheckout,
            contextBuyer,
            canViewCapacity,
          }),
          hasReviewedApplication: createHasReviewedApplication({
            contextBuyerID,
            isExchangedStreamlined,
            setHasReviewedApplication,
          }),
          processDeferredInitializationEvents,
          logError: createLogError({ merchantID }),
        },
        actors: {
          fetchApplication: createFetchApplication({ apiFetch }),
          fetchLatestApplication: createFetchLatestApplication({ apiFetch }),
          createApplication: createCreateApplication({
            apiFetch,
            order,
            locale,
            merchantOrigin,
            locationID,
            cartID,
            program,
          }),
          reCreateApplication: createReCreateApplication({
            apiFetch,
            order,
            locale,
            merchantOrigin,
            locationID,
            cartID,
            program,
          }),
          applicationCheckout: createApplicationCheckout({ apiFetch, order }),
          applicationPrepareCheckout: createApplicationPrepareCheckout({
            apiFetch,
            merchantPaymentProducts,
          }),
          prepareInStoreApplication,
          prepareVirtualCardIssuance: createPrepareVirtualCardIssuance({
            apiFetch,
            setVirtualCardIssuance,
          }),
          locateExchangedOfferAndGenerateDisclosures:
            createLocateExchangedOfferAndGenerateDisclosures({
              apiFetch,
              paymentAgreementID,
              namespace,
            }),
          reCreateInStoreContingentApplication:
            createReCreateInStoreContingentApplication({
              apiFetch,
              locale,
              merchantOrigin,
              locationID,
              cartID,
              program,
            }),
          getErrorStatusCodeFromApplication,
        },
        delays: {
          checkoutCompleteDelay: () => {
            return onCheckoutDelayMs;
          },
        },
      }),
      {
        input: initialContext,
      }
    );

    // Ensure we re-create application if order changes
    useOrderUpdateEventEmitter(applicationService, order, authState);

    useBuyerMachineEventEmitter(applicationService, buyerState);

    return (
      <ApplicationMachineContext.Provider value={applicationService}>
        {children}
      </ApplicationMachineContext.Provider>
    );
  };
